import { defineMessage, MessageDescriptor } from 'react-intl';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';

/**
 * What each service calls its artist programme, and what connecting it buys.
 *
 * The names are the services' own — an artist looking for "Spotify for Artists"
 * should find that string, not "Spotify". Spelled out one by one because the
 * extractor needs a static id per message.
 */
export interface CatalogServiceCardCopy {
  programName: MessageDescriptor;
  note: MessageDescriptor;
  /**
   * The note while the service is in early access, when it differs from
   * `note`. Spec D3: it never says that numbers arrive now, so it ends with
   * "when your account is on".
   */
  earlyAccessNote?: MessageDescriptor;
}

export const catalogServiceCardCopy: Record<string, CatalogServiceCardCopy> = {
  [MusicAPIIntegrationID.Spotify]: {
    programName: defineMessage({
      id: 'app.sml.catalog.connection.spotify.name',
      defaultMessage: 'Spotify for Artists',
    }),
    note: defineMessage({
      id: 'app.sml.catalog.connection.spotify.note',
      defaultMessage: 'Streams, listeners, playlist adds and pre-save delivery.',
    }),
  },
  [MusicAPIIntegrationID.AppleMusic]: {
    programName: defineMessage({
      id: 'app.sml.catalog.connection.apple_music.name',
      defaultMessage: 'Apple Music for Artists',
    }),
    note: defineMessage({
      id: 'app.sml.catalog.connection.apple_music.note',
      defaultMessage: 'Plays, Shazams and city-level listener data.',
    }),
    earlyAccessNote: defineMessage({
      id: 'app.sml.catalog.early_access.apple_music.note',
      defaultMessage: 'Plays, Shazams and city-level listener data, when your account is on.',
    }),
  },
  [MusicAPIIntegrationID.YoutubeMusic]: {
    programName: defineMessage({
      id: 'app.sml.catalog.connection.youtube_music.name',
      defaultMessage: 'YouTube Official Artist',
    }),
    note: defineMessage({
      id: 'app.sml.catalog.connection.youtube_music.note',
      defaultMessage: 'Views, likes, subscribers and your top videos, read every day.',
    }),
  },
  [MusicAPIIntegrationID.Tidal]: {
    programName: defineMessage({
      id: 'app.sml.catalog.connection.tidal.name',
      defaultMessage: 'Tidal Artist Home',
    }),
    note: defineMessage({
      id: 'app.sml.catalog.connection.tidal.note',
      defaultMessage: 'Adds Tidal streams and pre-save delivery.',
    }),
    earlyAccessNote: defineMessage({
      id: 'app.sml.catalog.early_access.tidal.note',
      defaultMessage: 'Tidal streams and pre-save delivery, when your account is on.',
    }),
  },
  [MusicAPIIntegrationID.Deezer]: {
    programName: defineMessage({
      id: 'app.sml.catalog.connection.deezer.name',
      defaultMessage: 'Deezer for Creators',
    }),
    note: defineMessage({
      id: 'app.sml.catalog.connection.deezer.note',
      defaultMessage: 'Adds Deezer numbers and pre-save delivery.',
    }),
  },
  [MusicAPIIntegrationID.AmazonMusic]: {
    programName: defineMessage({
      id: 'app.sml.catalog.connection.amazon_music.name',
      defaultMessage: 'Amazon Music for Artists',
    }),
    note: defineMessage({
      id: 'app.sml.catalog.connection.amazon_music.note',
      defaultMessage: 'Adds Amazon Music numbers and pre-save delivery.',
    }),
    earlyAccessNote: defineMessage({
      id: 'app.sml.catalog.early_access.amazon_music.note',
      defaultMessage: 'Amazon Music numbers and pre-save delivery, when your account is on.',
    }),
  },
  // design: connector-waitlist a (Connection Deck). Early access only.
  [MusicAPIIntegrationID.Boomplay]: {
    programName: defineMessage({
      id: 'app.sml.catalog.early_access.boomplay.name',
      defaultMessage: 'Boomplay for Artists',
    }),
    note: defineMessage({
      id: 'app.sml.catalog.early_access.boomplay.note',
      defaultMessage: 'Boomplay streams and listener countries, when your account is on.',
    }),
  },
  [MusicAPIIntegrationID.SoundCloud]: {
    programName: defineMessage({
      id: 'app.sml.catalog.early_access.sound_cloud.name',
      // Music24 reads the official totals, not "SoundCloud for Artists" (design soundcloud-connector a).
      defaultMessage: 'SoundCloud',
    }),
    note: defineMessage({
      id: 'app.sml.catalog.early_access.sound_cloud.note',
      defaultMessage: 'Plays, likes, reposts and comments of your tracks, read every day.',
    }),
  },
  [MusicAPIIntegrationID.Audiomack]: {
    programName: defineMessage({
      id: 'app.sml.catalog.early_access.audiomack.name',
      defaultMessage: 'Audiomack for Artists',
    }),
    note: defineMessage({
      id: 'app.sml.catalog.connection.audiomack.note',
      defaultMessage: 'Plays, listeners, favorites, playlist adds, followers and AMP earnings, read every day.',
    }),
    earlyAccessNote: defineMessage({
      id: 'app.sml.catalog.early_access.audiomack.note',
      defaultMessage: 'Plays, favorites and reposts, when your account is on.',
    }),
  },
};
